/* eslint-disable security/detect-unsafe-regex */
// don't include leading slash so we can compare safely with the split operator
// we can only check top level path segments as the second level can contain dynamic ids (e.g. app/[appId])
export const allowedPaths = [
  'enable-mfa',
  'login',
  'no-org',
  'verify-email',
  'link-account',
  'no-access',
  'analytics',
  'apps/[appId]',
  'apps',
  'automations/[flowId]',
  'automations',
  'dashboard',
  'feedback',
  'inboxes/browse',
  'inboxes',
  'integrations/browse',
  'integrations/inboxes/',
  'integrations/apps/',
  'integrations',
  'models/[modelId]',
  'models',
  'settings',
  'settings/account',
  'settings/apikeys',
  'settings/audit-logs',
  'settings/integrations/browse',
  'settings/integrations',
  'settings/members',
  'settings/members2',
  'settings/organization',
  'settings/security',
  'settings/sso',
];

export const allowedHosts = ['tekst.ai', 'tekst.com'];

// this function cleanses the next url provided to a login request and returns
// either the url if it is trusted, or the base path if the url is not trusted
export function cleanseUrl(url?: string, verbose = true) {
  // no url or empty string returns to the main page
  if (!url || typeof url !== 'string' || !url.length || url === 'null') {
    verbose && console.log('Invalid URL', url);
    return '/';
  }

  // html (script) tags are not allowed, nor script or javascript
  if (/[<>]|javascript|script/.test(url)) {
    verbose && console.log('Script tag detected', url);
    return '/';
  }

  // relative path url
  if (url.startsWith('/')) {
    const [path, params] = url.split('?');
    if (!validPath(path, verbose)) {
      verbose && console.log('Invalid path', url);
      return '/';
    }
    if (params && !validParams(params, verbose)) {
      verbose && console.log('Invalid params', url);
      return '/';
    }
  } else {
    try {
      // ref: https://nodejs.org/api/url.html#the-whatwg-url-api
      const parsedUrl = new URL(url);

      // check protocol
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
        verbose && console.log('Invalid protocol', url);
        return '/';
      }

      // check port
      if (parsedUrl.port !== '' && parsedUrl.port !== '443') {
        verbose && console.log('Invalid port', url);
        return '/';
      }

      // check hostname
      let trustedHost = false;
      for (const allowedHost of allowedHosts) {
        if (parsedUrl.hostname.endsWith(allowedHost)) {
          trustedHost = true;
          break;
        }
      }

      if (!trustedHost) {
        verbose && console.log('Invalid hostname', url);
        return '/';
      }

      // check search params, drop leading "?"
      const params = parsedUrl.search.substring(1);
      if (params.length > 1 && !validParams(params, verbose)) {
        verbose && console.log('Invalid params', url);
        return '/';
      }

      // check path
      if (!validPath(parsedUrl.pathname, verbose)) {
        verbose && console.log('Invalid path', url);
        return '/';
      }
    } catch {
      verbose && console.log('Parsing error', url);
      return '/';
    }
  }

  return url;
}

function validPath(urlPath: string, verbose: Boolean) {
  // drop first element, is always empty due to leading "/"
  const pathSegments = urlPath.split('/').slice(1);

  if (!allowedPaths.includes(pathSegments[0])) {
    return false;
  }

  // unusually long path segments are not allowed, uuid length is 36, some integrations are 40
  for (const pathSegment of pathSegments) {
    if (pathSegment.length > 40) {
      verbose && console.log('Path segment too long', urlPath);
      return false;
    }
  }

  return true;
}

function validParams(urlParams: string, verbose: Boolean) {
  const paramSegments = urlParams.split('&');

  for (const paramSegment of paramSegments) {
    const [_, paramValue] = paramSegment.split('=');

    // param values cannot be longer than 40 characters (UUID)
    // f.e. integrations=uuid,uuid
    if (paramValue.split('%').some((value) => value.length > 40)) {
      verbose && console.log('Param value too long', paramValue);
      return false;
    }

    if (paramValue.split('%').some((value) => isMaliciousUrlParam(decodeURIComponent(value)))) {
      verbose && console.log('param value contains malicious pattern', paramValue);
      return false;
    }
  }

  return true;
}

function isMaliciousUrlParam(str: string) {
  const patterns = [
    // SQL Injection patterns
    /(%27)|(')|(--)|(%23)|(#)/i, // SQL meta-characters
    /((%3D)|=)[^\n]*((%27)|(')|(--)|(%3B)|(;))/i, // SQL tautologies and statements
    /UNION/i, // UNION keyword
    /SELECT/i, // SELECT keyword
    /INSERT/i, // INSERT keyword
    /UPDATE/i, // UPDATE keyword
    /DELETE/i, // DELETE keyword
    /DROP/i, // DROP keyword
    /EXEC/i, // EXEC keyword
    /MERGE/i, // MERGE keyword
    /CALL/i, // CALL keyword
    /OR\s*(\d+)\s*=\s*\d+/i, // OR 1=1 patterns
    /--/g, // Comment indicator

    // XSS patterns
    /\.js$/gi,
    /\.css$/gi,
    /\.html$/gi,
    /\.htm$/gi,
    /\.php$/gi,
    /\.ts$/gi,
    /\.tsx$/gi,
    /\.vue$/gi,
    /\.jsx$/gi,
    /\.ts$/gi,
    /<script[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, // <script> tags
    /javascript:/i, // javascript: URI
    /on\w+\s*=/i, // Event handlers (e.g., onclick=)
    /alert/i, // alert() function
    /document/i, // document object
    /window/i, // window object
    /console/i, // console object
    /prompt/i, // prompt() function
    /confirm/i, // confirm() function
    /innerHTML/i, // innerHTML property
    /location/i, // location object
    /<\/?\w+:\w+/i, // Namespaced elements
    /onload/i, // onload event handler
    /onerror/i, // onerror event handler
    /onresize/i, // onresize event handler
    /onscroll/i, // onscroll event handler
    /\b(on\w*)/i, // Generic event handler
    /<iframe[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, // <iframe> tags
    /<img[^<]*(?:(?!>)<[^<]*)*onerror=/gi, // <img onerror=...> patterns
    /<link[^<]*(?:(?!>)<[^<]*)*rel=stylesheet/gi, // Stylesheet injection

    // Command Injection patterns
    /(?:nc|netcat|bash|sh|cmd|powershell|pwsh|curl|wget|tftp)/i, // Common command-line tools
    /(?:&|\||;|`|>|<|\$)/i, // Shell metacharacters
    /(?:rm|mv|cp|ls|echo|cat|head|tail|more|less)/i, // Common Unix commands
    /(?:ping|tracert|ipconfig|ifconfig)/i, // Network commands

    // Path Traversal patterns
    /(\.\.\/)+/i, // ../ sequences
    /\/etc\/passwd/i, // Attempt to access passwd file in Unix
    /\/proc\/self\/environ/i, // Attempt to access process environment
    /([a-zA-Z]:\\|\\\\)/i, // Windows absolute paths

    // LDAP Injection patterns
    /\(\)\s*\{\s*:\s*\|.*?\}/i, // Malicious LDAP filter
    /\|\||&&|\*|!.+/i, // LDAP operators
    /(?:cn|ou|dc|uid|dn)\s*=\s*[*?~|\\]/i, // Common LDAP attributes with wildcards

    // XML External Entity (XXE) patterns
    /<!DOCTYPE\s+\w+\s*\[\s*<!ENTITY\s+/i, // DOCTYPE with ENTITY
    /<!ENTITY\s+\w+\s+SYSTEM\s+/i, // ENTITY SYSTEM
    /<!ENTITY\s+\w+\s+PUBLIC\s+/i, // ENTITY PUBLIC
    /<!\[CDATA\[.*?\]\]>/i, // CDATA sections

    // Other dangerous patterns
    /data:text\/html/i, // data URIs
    /base64,/i, // base64 encoding in URIs
    /exec/i, // exec function
    /eval/i, // eval function
    /expression/i, // CSS expressions
    /import/i, // import statements
    /require/i, // require statements
    /fetch/i, // fetch API usage
    /document\.cookie/i, // Accessing cookies
    /Set-Cookie/i, // Set-Cookie header
    /HTTPOnly/i, // HTTPOnly flag in cookies
    /Secure/i, // Secure flag in cookies
  ];

  for (const pattern of patterns) {
    if (pattern.test(str)) {
      return true;
    }
  }

  return false;
}
